import { URL_TYPES } from '@wix/wix-vod-constants/common';
import { PRODUCTION_HOSTS } from '@wix/wix-vod-constants/env';
import { parseChannelUrl, parseVideoUrl } from './utils/parse-url';
class YoutubeService {
    constructor(httpClient) {
        this.httpClient = httpClient;
        this.MAX_IDS_IN_REQUEST = 50;
        this.channelIdToPlaylistIdMap = new Map();
    }
    async get(url, requestOptions) {
        const params = typeof window !== 'undefined' &&
            window.hasOwnProperty('BusinessManagerAPI')
            ? {
                artifact: 'wix-vod-library',
            }
            : undefined;
        return this.httpClient.get(PRODUCTION_HOSTS.pixWix + url, Object.assign({ params: Object.assign(Object.assign({}, params), requestOptions === null || requestOptions === void 0 ? void 0 : requestOptions.params) }, requestOptions));
    }
    fetchChannels(options, requestConfig) {
        options = Object.assign({ order: 'relevance', ids: [], maxResults: this.MAX_IDS_IN_REQUEST, shouldThrowWhenNoItems: false }, options);
        const params = {
            pageToken: options.cursor,
            order: options.order,
            forUsername: options.userName,
            // send all ids in '&id=1,2,3" format
            id: options.ids && options.ids.length ? options.ids.join(',') : null,
            maxResults: options.maxResults,
            shouldThrowWhenNoItems: options.shouldThrowWhenNoItems,
        };
        return this.get('/youtube/channels', Object.assign({ params }, requestConfig));
    }
    fetchPlayLists(options = {}, requestConfig) {
        const params = {
            shouldThrowWhenNoItems: options.shouldThrowWhenNoItems || false,
            id: options.ids && options.ids.length ? options.ids.join(',') : null,
        };
        return this.get('/youtube/playlists', Object.assign({ params }, requestConfig));
    }
    makeChunks(ids, size) {
        return ids.reduce((acc, id) => {
            const lastChunkIndex = acc.length - 1;
            if (acc[lastChunkIndex].length === size) {
                acc.push([id]);
            }
            else {
                acc[lastChunkIndex].push(id);
            }
            return acc;
        }, [[]]);
    }
    batchFetch(ids, fetcher) {
        const chunks = this.makeChunks(ids, this.MAX_IDS_IN_REQUEST);
        const requests = chunks.map((channelIds) => fetcher(channelIds));
        return Promise.all(requests);
    }
    parseChannelUrl(url) {
        return parseChannelUrl(url);
    }
    parseVideoUrl(url) {
        return parseVideoUrl(url);
    }
    getChannelsByIds(ids, requestConfig) {
        return this.batchFetch(ids, (chunk) => this.fetchChannels({
            ids: chunk,
        }, requestConfig)).then((responses) => {
            return Object.assign(Object.assign({}, responses[0]), { data: {
                    items: responses.reduce((acc, response) => response.data && response.data.items
                        ? acc.concat(response.data.items)
                        : acc, []),
                } });
        });
    }
    getChannelById(id, requestConfig) {
        return this.getChannelsByIds([id], requestConfig).then((response) => {
            return Object.assign(Object.assign({}, response), { data: response.data.items[0] });
        });
    }
    getChannelsByUsername(userName, requestConfig) {
        return this.fetchChannels({ userName }, requestConfig);
    }
    getPlayListsByIds(ids, requestConfig) {
        // TODO: add shouldThrowWhenNoItems to parameters
        return this.batchFetch(ids, (chunk) => this.fetchPlayLists({ ids: chunk }, requestConfig)).then((responses) => {
            return Object.assign(Object.assign({}, responses[0]), { data: {
                    items: responses.reduce((acc, response) => response.data && response.data.items
                        ? acc.concat(response.data.items)
                        : acc, []),
                } });
        });
    }
    getPlaylistById(id, requestConfig) {
        return this.getPlayListsByIds([id], requestConfig).then((response) => {
            return Object.assign(Object.assign({}, response), { data: response.data.items[0] });
        });
    }
    getPlaylistIdByChannelId(channelId, requestConfig) {
        const cachedPlaylistId = this.channelIdToPlaylistIdMap.get(channelId);
        if (cachedPlaylistId) {
            return Promise.resolve(cachedPlaylistId);
        }
        return this.getChannelById(channelId, requestConfig).then(({ data }) => {
            const dta = data;
            const playlistId = dta.contentDetails.relatedPlaylists.uploads;
            this.channelIdToPlaylistIdMap.set(channelId, playlistId);
            return playlistId;
        });
    }
    fetchVideos(videoIds, requestConfig) {
        const requestOptions = Object.assign({ params: {
                id: videoIds.join(','),
            } }, requestConfig);
        return this.get('/youtube/videos', requestOptions);
    }
    getVideosByIds(videoIds, requestConfig) {
        return this.batchFetch(videoIds, (idsChunk) => this.fetchVideos(idsChunk, requestConfig)).then((responses) => {
            return Object.assign(Object.assign({}, responses[0]), { data: {
                    items: responses.reduce((acc, response) => acc.concat(response.data.items), []),
                } });
        });
    }
    doFetchPlaylistItems(playlistId, maxResults, pageToken, requestConfig) {
        return this.get('/youtube/playlistitems', Object.assign({ params: {
                maxResults,
                pageToken,
                playlistId,
            } }, requestConfig));
    }
    async fetchChannelVideosByPlaylistId({ playlistId, itemsCount, pageToken = '' }, requestConfig) {
        let items = [];
        let perPage = Math.min(itemsCount, this.MAX_IDS_IN_REQUEST);
        let lastResponse;
        const recalculate = (response) => {
            items = items.concat(response.data.items);
            itemsCount = Math.max(itemsCount - response.data.items.length, 0);
            pageToken = response.data.nextPageToken;
            perPage = Math.min(itemsCount, this.MAX_IDS_IN_REQUEST);
        };
        lastResponse = await this.doFetchPlaylistItems(playlistId, perPage, pageToken, requestConfig);
        recalculate(lastResponse);
        while (itemsCount && pageToken) {
            lastResponse = await this.doFetchPlaylistItems(playlistId, perPage, pageToken, requestConfig);
            recalculate(lastResponse);
        }
        lastResponse.data.items = items;
        return lastResponse;
    }
    async getPlaylistItems({ externalId, urlType, pageToken = '', itemsCount, }, requestConfig) {
        if (urlType === URL_TYPES.CHANNEL) {
            externalId = await this.getPlaylistIdByChannelId(externalId, requestConfig);
        }
        return this.fetchChannelVideosByPlaylistId({
            playlistId: externalId,
            itemsCount,
            pageToken: pageToken !== null && pageToken !== void 0 ? pageToken : undefined,
        }, requestConfig);
    }
    getCategoryList(categoryIds, requestConfig) {
        const ids = categoryIds.join(',');
        return this.get('/youtube/categories', Object.assign({ params: {
                ids,
            } }, requestConfig));
    }
    async getVideoById(videoId, requestConfig) {
        const response = await this.fetchVideos([videoId], requestConfig);
        return Object.assign(Object.assign({}, response), { data: Object.assign(Object.assign({}, response.data), { categoryItems: await this.fetchCategoryItemsForVideos(response.data.items, requestConfig) }) });
    }
    async fetchCategoryItemsForVideos(items, requestConfig) {
        const categoryIds = items.map((item) => item.snippet.categoryId);
        const uniqueCategoryIds = Array.from(new Set(categoryIds));
        try {
            const { data } = await this.getCategoryList(uniqueCategoryIds, requestConfig);
            return data.items;
        }
        catch (e) {
            return [];
        }
    }
    async fetchChannelItemsForVideos(items, requestConfig) {
        const channelIds = items.map((item) => item.snippet.channelId);
        try {
            const { data } = await this.getChannelsByIds(channelIds, requestConfig);
            return data.items;
        }
        catch (e) {
            return [];
        }
    }
    async getChannelVideos({ externalId, urlType, pageToken = '', itemsCount, }, requestConfig) {
        const response = await this.getPlaylistItems({ externalId, urlType, pageToken, itemsCount }, requestConfig);
        const playlistItems = response.data.items;
        const videoIds = playlistItems.map((item) => item.contentDetails.videoId);
        const videosResponse = await this.getVideosByIds(videoIds, requestConfig);
        const videoItems = videosResponse.data.items;
        const [categoryItems, channelItems] = await Promise.all([
            this.fetchCategoryItemsForVideos(videoItems, requestConfig),
            this.fetchChannelItemsForVideos(videoItems, requestConfig),
        ]);
        return Object.assign(Object.assign({}, response), { data: {
                playlistItems,
                videoItems,
                channelItems,
                categoryItems,
                nextPageToken: response.data.nextPageToken,
                pageInfo: response.data.pageInfo,
            } });
    }
}
export { YoutubeService };
//# sourceMappingURL=youtube.js.map